import './App.css';
import Home from './routes/Home';
import Login from './routes/Login';
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { createContext, useEffect, useReducer, useState } from 'react';
import moment from 'moment';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import LoadingDialog from './components/LoadingDialog';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useApi from './api';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import 'moment/locale/es';
import { consultarCuenta } from './api/cuenta';
import { isNumber, isObject } from 'lodash';

import ListadoRegistrosTag from './pages/tags/ListadoRegistrosTag';
import ListadoRegistrosCarril from './pages/carriles/ListadoRegistrosCarril';
import ListadoEstanciasAbiertas from './pages/estancias/ListadoEstanciasAbiertas';
import { MainContainer } from './pages/MainContainer';

const cuentaInitialState = {
    loaded: false,
    loggedIn: false,
    permisos: [],
}

export const APP_RELOAD_SESSION = 'needUpdateSession';
export const APP_RELOAD_CLIENTE = 'reloadCliente';

//Se recomienda usar valores numéricos para "escuchar" el incremento del valor
const appInitialState = {
    [APP_RELOAD_SESSION]: 0,
    [APP_RELOAD_CLIENTE]: 0,
}

const appContextInitialState = {
    cuenta: {
        ...cuentaInitialState,
    },
    app: {
        ...appInitialState,
    },
    notificacion: {},
    notificaciones: [],
    notificar: () => { },
    notificarInfo: () => { },
    notificarError: () => { },
    notificarWarn: () => { },
    actualizaApp: () => { },
    actualizaCuenta: () => { },
    tieneCuentaRoles: () => { },
    tieneCuentaPermisos: () => { },
    wipeAllData: () => { },
    showLoadingDialog: () => { },
}

export const AppContext = createContext(appContextInitialState);

const MainApp = () => {

    const [state, dispatch] = useReducer((state, action) => {
        switch (action.type) {
            case 'UPDATE_APP':
                return { ...state, app: { ...state.app, ...action.payload } };
            case 'UPDATE_CUENTA':
                return { ...state, cuenta: { ...state.cuenta, ...action.payload } };
            case 'CLEAR_APP':
                return { ...state, app: { ...appContextInitialState.app } };
            case 'CLEAR_CUENTA':
                return { ...state, cuenta: { ...appContextInitialState.cuenta } };
            default:
                return state;
        }
    }, appContextInitialState);

    const { app, cuenta } = state;
    const navigate = useNavigate();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [showLoading, setShowLoading] = useState(false);

    const [{ data: cuentaData, loading: loadingCuenta, error: cuentaError }, cuentaApi] = useApi();

    const loggedIn = cuenta.loggedIn || false;

    useEffect(() => {
        console.log("MainApp...");
        // const browser = detect();
        // console.log(browser);
        //Ajustes para moment GLOBAL...
        moment.updateLocale('es', { week: { dow: 0 } }) //La semana va de dom-sab
    }, [])

    useEffect(() => {
        //Se venció la sesion... a firmarse de nuevo
        if (app.needUpdateSession > 0) {
            wipeAllData();
            // history.replace("/");
            // setCuenta(cuentaInitialState);
            //actualizaApp({ needUpdateSession: false });
        }
    }, [app.needUpdateSession]);

    useEffect(() => {
        if (cuenta.loaded !== true && loadingCuenta !== true) {
            console.log("Consultando cuenta...");
            cuentaApi(consultarCuenta());
        }
    }, [cuenta]);

    useEffect(() => {
        if (cuentaData) {
            //console.log("cuentaData", cuentaData, grupoOperador);
            actualizaCuenta({
                ...cuenta,
                ...cuentaData,
                loaded: true,
                loggedIn: true,
            })
        }
    }, [cuentaData]);

    useEffect(() => {
        if (cuentaError && cuentaError.response) {
            if (cuentaError.response.status === 401) {
                //wipeAllData();
                navigate("/", { replace: true });
            }
        }
    }, [cuentaError]);

    //Funciones para el AppContext
    const notificar = (msg, options = { variant: 'default' }) => {
        return msg ? enqueueSnackbar(msg, options) : null;
    }

    const notificarError = (msg) => {
        return notificar(msg, { variant: 'error', action: key => (<IconButton color="inherit" onClick={() => { closeSnackbar(key) }}><CloseIcon /></IconButton>) });
    }

    const notificarInfo = (msg) => {
        return notificar(msg, { variant: 'info' });
    }

    const notificarWarn = (notificacion) => {
        return notificar(notificacion, { variant: 'warning' });
    }

    const actualizaApp = (key, value) => {
        // La ides es incrementar el valor de la llave en uno, si no se especifica un valor
        const currentValue = app[key];
        const nextValue = isNil(value) ? (isNumber(currentValue) ? (currentValue + 1) : value) : value;
        const newState = isObject(value) ? value : { [key]: nextValue };
        dispatch({
            type: 'UPDATE_APP',
            payload: {
                ...app,
                ...newState,
            }
        });
    }

    const actualizaCuenta = (value) => {
        dispatch({
            type: isNil(value) ? 'CLEAR_CUENTA' : 'UPDATE_CUENTA',
            payload: {
                ...cuenta,
                ...value,
            }
        });
    }

    const tieneCuentaRoles = (roles = []) => {
        if (!isArray(roles)) {
            roles = [roles];
        }

        if (roles.length > 0 && cuenta && cuenta.roles && cuenta.roles.length > 0) {
            const cuentaRoles = cuenta.roles.filter(p => roles.includes(p));
            return cuentaRoles.length > 0;
        }
        return false;
    }

    const tieneCuentaPermisos = (permisos = []) => {
        if (!isArray(permisos)) {
            permisos = [permisos];
        }

        if (permisos.length > 0 && cuenta && cuenta.permisos && cuenta.permisos.length > 0) {
            const cuentaPermisos = cuenta.permisos.filter(p => permisos.includes(p));
            return cuentaPermisos.length > 0;
        }
        return false;
    }

    const wipeAllData = () => {
        actualizaCuenta();
        actualizaApp();
    }

    const showLoadingDialog = open => {
        setShowLoading(open);
    }

    //Funciones Locales...
    const canGoToIndex = () => {
        return loggedIn === true;
    }

    const showLogin = () => {
        const noFirmadoNoCargandoCuenta = loggedIn === false && loadingCuenta !== true;
        return noFirmadoNoCargandoCuenta;
    }


    return (

        <AppContext.Provider value={{
            ...state,
            notificar,
            notificarInfo,
            notificarError,
            notificarWarn,
            actualizaApp,
            actualizaCuenta,
            tieneCuentaRoles,
            tieneCuentaPermisos,
            wipeAllData,
            showLoadingDialog,
        }}>
            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale='es'>
                <div>
                    <Routes>
                        {showLogin() &&
                            <Route path="/" element={<Login />} />
                        }
                        {canGoToIndex() &&
                            <>
                                <Route path="/" element={<MainContainer />}>
                                    <Route index element={<Home />} />
                                    <Route path="/registros/tags/:prefijo/:numero/*" element={<ListadoRegistrosTag />} />
                                    <Route path="/registros/carril/:carrilId/*" element={<ListadoRegistrosCarril />} />
                                    <Route path="/estanciasAbiertas/*" element={<ListadoEstanciasAbiertas />} />
                                </Route>
                            </>
                        }
                    </Routes>
                </div>
            </LocalizationProvider>
            <LoadingDialog open={showLoading} />
        </AppContext.Provider>

    )
}

const App = () => {
    return (
        <SnackbarProvider maxSnack={3} autoHideDuration={5000}>
            <BrowserRouter basename={process.env.PUBLIC_URL}>
                <Routes>
                    <Route path="/*" element={<MainApp />} />
                </Routes>
            </BrowserRouter>
        </SnackbarProvider>
    );
}

export default App;
